/**
 * Toasts — feedback for an action whose result is not already on screen.
 *
 * A toast is the right answer for "saved" and the wrong answer for "that
 * failed, here is what to fix": it disappears, and an error the user needs to
 * act on must not. So errors here get no auto-dismiss, and anything with field
 * detail belongs on the form instead.
 *
 * Implemented as a store rather than a context so a non-React caller — a query
 * client's `onError`, an API interceptor — can raise one without being inside
 * the tree.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';

import { cn } from '../../lib/cn';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds. `null` never dismisses itself. */
  duration: number | null;
  action?: { label: string; onClick: () => void };
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'duration'> & { duration?: number | null }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

/** How many are visible at once. Beyond this the oldest is dropped. */
const MAX_VISIBLE = 4;

const DEFAULT_DURATION: Record<ToastTone, number | null> = {
  success: 3500,
  info: 4000,
  warning: 6000,
  // Errors stay. See the module docstring.
  error: null,
};

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const entry: Toast = {
      id,
      tone: toast.tone,
      title: toast.title,
      description: toast.description,
      action: toast.action,
      duration: toast.duration === undefined ? DEFAULT_DURATION[toast.tone] : toast.duration,
    };
    set((state) => ({ toasts: [...state.toasts, entry].slice(-MAX_VISIBLE) }));
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/**
 * Raise a toast from anywhere, React or not.
 *
 * `toast.error` takes the message the API already wrote. The API is responsible
 * for a message a person can act on, so passing it through verbatim is correct
 * rather than lazy — inventing a friendlier one here discards the specifics.
 */
export const toast = {
  success: (title: string, description?: string) =>
    useToasts.getState().push({ tone: 'success', title, description }),
  error: (title: string, description?: string) =>
    useToasts.getState().push({ tone: 'error', title, description }),
  warning: (title: string, description?: string) =>
    useToasts.getState().push({ tone: 'warning', title, description }),
  info: (title: string, description?: string) =>
    useToasts.getState().push({ tone: 'info', title, description }),
  dismiss: (id: string) => useToasts.getState().dismiss(id),
};

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: TriangleAlert,
  info: Info,
} as const;

const STYLES: Record<ToastTone, string> = {
  success: 'border-success/30 bg-success-subtle text-success',
  error: 'border-danger/30 bg-danger-subtle text-danger',
  warning: 'border-warning/30 bg-warning-subtle text-warning',
  info: 'border-info/30 bg-info-subtle text-info',
};

function ToastCard({ toast: entry }: { toast: Toast }) {
  const dismiss = useToasts((state) => state.dismiss);
  const Icon = ICONS[entry.tone];

  useEffect(() => {
    if (entry.duration === null) return;
    const timer = window.setTimeout(() => dismiss(entry.id), entry.duration);
    return () => window.clearTimeout(timer);
  }, [entry.id, entry.duration, dismiss]);

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-80 animate-slide-up gap-2.5 rounded-card border bg-surface p-3 shadow-raised',
        STYLES[entry.tone],
      )}
      role={entry.tone === 'error' ? 'alert' : 'status'}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium text-heading">{entry.title}</p>
        {entry.description ? (
          <p className="text-xs leading-relaxed text-muted">{entry.description}</p>
        ) : null}
        {entry.action ? (
          <button
            type="button"
            onClick={() => {
              entry.action?.onClick();
              dismiss(entry.id);
            }}
            className="text-xs font-medium underline underline-offset-2"
          >
            {entry.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => dismiss(entry.id)}
        className="-m-1 shrink-0 self-start rounded p-1 text-faint hover:text-body"
        aria-label="Dismiss"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/**
 * The toast region. Mounted once, near the root.
 *
 * `aria-live="polite"` on the container rather than on each toast: a live
 * region has to exist in the DOM before content is inserted into it, or a
 * screen reader never announces the first one.
 */
export function Toaster() {
  const toasts = useToasts((state) => state.toasts);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-toast flex flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((entry) => (
        <ToastCard key={entry.id} toast={entry} />
      ))}
    </div>,
    document.body,
  );
}
